import React from "react";
import { useRBACPermission, useCanPerformAnyAction } from "../../hooks/useRBACPermission";
import { ModuleType, ROLE_DISPLAY_NAMES } from "../../constants/rbac";
import { useAuthStore } from "../../stores/authStore";

interface RBACButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  module: ModuleType;
  action: string;
  children: React.ReactNode;
  /**
   * Show tooltip message when permission denied
   */
  deniedMessage?: string;
  /**
   * Render as disabled instead of hiding (default: false)
   */
  showAsDisabled?: boolean;
  /**
   * Tooltip content
   */
  tooltip?: string;
}

/**
 * Button that enforces RBAC permissions
 * Either hides the button or shows it as disabled based on showAsDisabled prop
 */
export const RBACButton = React.forwardRef<HTMLButtonElement, RBACButtonProps>(
  (
    {
      module,
      action,
      children,
      deniedMessage,
      showAsDisabled = false,
      disabled,
      title,
      className = "",
      ...props
    },
    ref
  ) => {
    const hasPermission = useRBACPermission(module, action);
    const user = useAuthStore((state) => state.user);

    if (!hasPermission && !showAsDisabled) {
      return null;
    }

    const tooltipText = !hasPermission
      ? deniedMessage || `No permission to ${action} ${module}`
      : title;

    const roles = user?.roles?.map((r) => ROLE_DISPLAY_NAMES[r.toLowerCase() as any] || r).join(", ");
    const finalTitle = !hasPermission && roles ? `${tooltipText} (Your roles: ${roles})` : tooltipText;

    return (
      <button
        ref={ref}
        disabled={disabled || !hasPermission}
        title={finalTitle}
        className={`${className} ${!hasPermission ? "opacity-50 cursor-not-allowed" : ""}`}
        {...props}
      >
        {children}
      </button>
    );
  }
);

RBACButton.displayName = "RBACButton";

interface RBACActionProps {
  module: ModuleType;
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Conditionally render content based on RBAC permission
 */
export const RBACAction: React.FC<RBACActionProps> = ({
  module,
  action,
  children,
  fallback = null,
}) => {
  const hasPermission = useRBACPermission(module, action);
  return <>{hasPermission ? children : fallback}</>;
};

interface RBACMultiActionProps {
  checks: [ModuleType, string][];
  type?: "all" | "any";
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Check multiple permissions with AND/OR logic
 */
export const RBACMultiAction: React.FC<RBACMultiActionProps> = ({
  checks,
  type = "any",
  children,
  fallback = null,
}) => {
  const hasPermission = useCanPerformAnyAction(checks);

  if (type === "any") {
    return <>{hasPermission ? children : fallback}</>;
  }

  // TODO: Implement "all" logic
  return <>{children}</>;
};

interface RBACMenuItemProps {
  module: ModuleType;
  action: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  className?: string;
  dangerColor?: boolean;
}

/**
 * Menu item that respects RBAC
 */
export const RBACMenuItem: React.FC<RBACMenuItemProps> = ({
  module,
  action,
  label,
  icon,
  onClick,
  className = "",
  dangerColor = false,
}) => {
  const hasPermission = useRBACPermission(module, action);

  if (!hasPermission) return null;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
        dangerColor
          ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
          : "hover:bg-muted"
      } ${className}`}
    >
      {icon && <span className="size-4">{icon}</span>}
      <span className="text-sm">{label}</span>
    </button>
  );
};
